import React, { useCallback, useMemo, useState } from 'react';
import type { ExtendedComponentSchema } from 'formiojs';
import { Components, Utils } from 'formiojs';
import type {
  EhsFormioReactProps,
  FormioBuilderInfo,
  FormioBuilderSchema
} from '../';
import { EhsFormioReactComponent } from '../';
import { TextInput } from '../../../form';
import { intlFmtTemplateString } from '../../../../lib';
import type { Component } from '@formio/react';
import { useQuestionnaireValidation } from '../../questionnaire/useQuestionnaireValidation';

//START-EHS-REACT-FORMIO-DEFINITION---------------------------------------------
export type EhsFormioTextInputConstructor = {
  classNames: string;
  multiline: boolean;
  multilineRows: number;
  initialValue: string;
};
/**
 * @param classNames a string separated by spaces that will be added as classNames
 * @param multiline if it's a multiline input or not
 * @param multilineRows number of rows the textarea should take if multiline is true
 * @param initialValue the value, the field will be prefilled with
 */
export type EhsFormioTextInputSettings = {
  classNames?: string;
  multiline: boolean;
  multilineRows?: number;
  initialValue?: string;
};
export const SimpleConditional = {
  type: 'panel',
  title: 'Conditional',
  tooltip:
    'Set the values accordingly, if the component should render, depending on the value of another field',
  key: 'simple-conditional',
  theme: 'default',
  weight: 105,
  components: [
    {
      type: 'select',
      input: true,
      label: 'This component should Display:',
      key: 'conditional.show',
      dataSrc: 'values',
      data: {
        values: [
          { label: 'True', value: 'true' },
          { label: 'False', value: 'false' }
        ]
      }
    },
    {
      type: 'select',
      input: true,
      label: 'When the form component:',
      key: 'conditional.when',
      dataSrc: 'custom',
      valueProperty: 'value',
      data: {
        custom(context: any) {
          return Utils.getContextComponents(context);
        }
      }
    },
    {
      type: 'textfield',
      input: true,
      label: 'Has the value:',
      key: 'conditional.eq'
    }
  ]
};
export const EhsFormioTextInputType = 'ehsTextInput';
export class EhsFormioTextInput extends EhsFormioReactComponent<
  EhsFormioTextInputSettings,
  EhsFormioTextInputConstructor
> {
  /**
   * defines the default schema of the custom component
   * https://help.form.io/developers/custom-components#component-methods
   */
  static schema(
    extend?: FormioBuilderSchema
  ): ExtendedComponentSchema<FormioBuilderSchema> {
    return Components.components.field.schema({
      type: EhsFormioTextInputType,
      label: 'common.unknown',
      key: EhsFormioTextInputType,
      classNames: '',
      multiline: false,
      multilineRows: 5,
      initialValue: '',
      ...extend
    });
  }
  /**
   * defines how the component will look in the Form Editor (Builder)
   * https://help.form.io/developers/custom-components#component-methods
   */
  static get builderInfo(): FormioBuilderInfo {
    return {
      title: 'Ehs-TextInput',
      group: 'advanced',
      icon: 'terminal',
      documentation: '/developers/custom-components',
      weight: 40,
      schema: EhsFormioTextInput.schema()
    };
  }

  /**
   * defines the fields available in the Editor/Builder
   * https://help.form.io/developers/form-builder#overriding-behavior
   */
  static editForm() {
    return {
      components: [
        {
          type: 'textfield',
          key: 'label',
          label: 'label',
          tooltip: 'Please add only valid translation keys'
        },
        {
          type: 'textfield',
          key: 'classNames',
          label: 'classNames',
          tooltip:
            ' A string separated by spaces that will be added as classNames',
          weight: 20
        },
        {
          type: 'checkbox',
          key: 'multiline',
          label: 'multiline',
          tooltip: "If it's a multiline input or not",
          weight: 30
        },
        {
          type: 'number',
          key: 'multilineRows',
          label: 'multilineRows',
          tooltip:
            'Number of rows the textarea should take if multiline is true.',
          weight: 31
        },
        {
          type: 'textfield',
          key: 'initialValue',
          label: 'initialValue',
          tooltip: 'The value, the field will be prefilled with',
          weight: 40
        },
        SimpleConditional
      ]
    };
  }

  //Begin Non static definitions-------------------------------------
  private classNames = '';
  private multiline = false;
  private multilineRows = 5;
  private initialValue = '';
  extractFields(component: Component<EhsFormioTextInputConstructor>): void {
    this.classNames = component.classNames;
    this.multiline = component.multiline;
    this.multilineRows = component.multilineRows;
    this.initialValue = component.initialValue ?? component.defaultValue ?? '';
    this.shouldSetValue = true;
    this.dataForSetting = this._data[component.key as string];
  }

  attachEhsReact(): [
    (props: EhsFormioReactProps<EhsFormioTextInputSettings>) => JSX.Element,
    EhsFormioTextInputSettings
  ] {
    return [
      TextInputCmp,
      {
        classNames: this.classNames,
        multiline: this.multiline,
        multilineRows: this.multilineRows,
        initialValue: this.initialValue
      }
    ];
  }
}
//END-EHS-REACT-FORMIO-DEFINITION-----------------------------------------------

/**
 * The Logic for the Formio-Component.
 *
 * @param params formio {@link EhsFormioTextInputSettings} properties
 */
function TextInputCmp({
  value,
  onChange,
  label,
  readOnly,
  required,
  settings,
  keyForComponent
}: EhsFormioReactProps<EhsFormioTextInputSettings>): JSX.Element {
  const { errorMessage, isQuestionnaire } = useQuestionnaireValidation(
    keyForComponent
  );

  const maxLength = 500;
  const [errorString, setErrorString] = useState('');
  const onChangeWrapper = useCallback(
    (val: string): void => {
      if (isQuestionnaire) {
        onChange(val);
        return;
      }

      // These checks are only for the formio component used within Stroke.
      if (val.length <= maxLength) {
        setErrorString('');
        onChange(val);
      } else {
        setErrorString(
          intlFmtTemplateString('react.field.string.max', {
            max: maxLength.toString()
          })
        );
      }
    },
    [isQuestionnaire, onChange]
  );
  const wrapperValue = useMemo(() => {
    return value ?? settings.initialValue ?? undefined;
  }, [settings.initialValue, value]);
  return useMemo(() => {
    return (
      <TextInput
        type="text"
        onChange={onChangeWrapper}
        value={wrapperValue}
        label={label}
        readOnly={readOnly}
        className={settings.classNames}
        error={errorString || errorMessage}
        multiline={settings.multiline}
        multilineRows={settings.multilineRows}
        required={required}
        labelOnTop={isQuestionnaire}
      />
    );
  }, [
    errorMessage,
    errorString,
    label,
    onChangeWrapper,
    readOnly,
    required,
    settings.classNames,
    settings.multiline,
    settings.multilineRows,
    wrapperValue,
    isQuestionnaire
  ]);
}
